/*! JointJS+ v4.2.2 (2026-01-22) - HTML5 Diagramming Framework

Copyright (c) 2025 client IO

This Source Code Form is subject to the terms of the JointJS+
License, v. 2.0. If a copy of the JointJS+ License was not
distributed with this file, You can obtain one at
https://www.jointjs.com/license or from the JointJS+ archive as was
distributed by client IO. See the LICENSE file.
*/
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import * as joint from '@joint/plus';
import { DirectedGraph } from '@joint/layout-directed-graph';
import { fontsStyleSheet } from '../config/font-style-sheet';
var ToolbarService = /** @class */ (function () {
    function ToolbarService(element) {
        this.element = element;
        // Keep track of open dropdowns
        this.openDropdowns = {
            file: false,
            layout: false,
            settings: false,
            share: false
        };
    }
    ToolbarService.prototype.create = function (commandManager, paperScroller, graph, paper) {
        var _this = this;
        var _a = this.getToolbarConfig(), tools = _a.tools, groups = _a.groups;
        this.toolbar = new joint.ui.Toolbar({
            groups: groups,
            tools: tools,
            autoToggle: true,
            references: {
                paperScroller: paperScroller,
                commandManager: commandManager
            },
            el: this.element,
        });
        this.toolbar.render();
        this.fileTools = [
            {
                action: 'new',
                content: 'New file'
            },
            {
                action: 'load',
                content: 'Load file'
            },
            {
                action: 'save',
                content: 'Save file'
            }
        ];
        this.shareTools = [
            {
                action: 'exportPNG',
                content: 'Export as PNG'
            },
            {
                action: 'exportSVG',
                content: 'Export as SVG'
            },
            {
                action: 'print',
                content: 'Print'
            }
        ];
        this.layoutTools = [
            {
                action: 'layout-tb',
                content: 'Top to bottom'
            },
            {
                action: 'layout-bt',
                content: 'Bottom to top'
            },
            {
                action: 'layout-lr',
                content: 'Left to right'
            },
            {
                action: 'layout-rl',
                content: 'Right to left'
            }
        ];
        this.toolbar.on({
            'select-file:pointerclick': function () { return _this.openFileSelect(commandManager, graph); },
            'select-share:pointerclick': function () { return _this.openShareSelect(paper); },
            'select-layout:pointerclick': function () { return _this.openLayoutSelect(graph, paperScroller); },
            'select-canvas-settings:pointerclick': function () { return _this.openSettingsPopup(graph); }
        });
    };
    ToolbarService.prototype.getToolbarConfig = function () {
        return {
            groups: {
                left: {
                    index: 1,
                    align: joint.ui.Toolbar.Align.Left
                },
                right: {
                    index: 2,
                    align: joint.ui.Toolbar.Align.Right
                },
            },
            tools: [
                {
                    type: 'button',
                    name: 'select-file',
                    group: 'left',
                    attrs: {
                        button: {
                            'data-tooltip': 'File',
                            'data-tooltip-position': 'top',
                            'data-tooltip-position-selector': '.toolbar-container'
                        }
                    }
                },
                {
                    type: 'separator',
                    group: 'left',
                },
                {
                    type: 'undo',
                    name: 'undo',
                    group: 'left',
                    attrs: {
                        button: {
                            'data-tooltip': 'Undo',
                            'data-tooltip-position': 'top',
                            'data-tooltip-position-selector': '.toolbar-container'
                        }
                    }
                },
                {
                    type: 'redo',
                    name: 'redo',
                    group: 'left',
                    attrs: {
                        button: {
                            'data-tooltip': 'Redo',
                            'data-tooltip-position': 'top',
                            'data-tooltip-position-selector': '.toolbar-container'
                        }
                    }
                },
                {
                    type: 'separator',
                    group: 'left',
                },
                {
                    type: 'button',
                    name: 'select-layout',
                    group: 'left',
                    text: 'Layout'
                },
                {
                    type: 'button',
                    text: 'Canvas settings',
                    name: 'select-canvas-settings',
                    group: 'right'
                },
                {
                    type: 'separator',
                    group: 'right',
                },
                {
                    type: 'button',
                    name: 'select-share',
                    group: 'right',
                    attrs: {
                        button: {
                            'data-tooltip': 'Share',
                            'data-tooltip-position': 'top',
                            'data-tooltip-position-selector': '.toolbar-container'
                        }
                    }
                }
            ],
        };
    };
    ToolbarService.prototype.getSettingsInspectorConfig = function () {
        return {
            inputs: {
                paperColor: {
                    type: 'color',
                    label: 'Paper color'
                },
                snaplines: {
                    type: 'toggle',
                    label: 'Snaplines'
                },
                infinitePaper: {
                    type: 'toggle',
                    label: 'Infinite paper',
                },
                dotGrid: {
                    type: 'toggle',
                    label: 'Dot grid',
                },
                gridSize: {
                    type: 'range',
                    label: 'Grid size',
                    min: 1,
                    max: 50,
                    step: 1
                }
            }
        };
    };
    ToolbarService.prototype.layoutDirectedGraph = function (rankDir, graph, paperScroller) {
        DirectedGraph.layout(graph, {
            setVertices: true,
            rankDir: rankDir,
            marginX: 100,
            marginY: 100
        });
        paperScroller.centerContent({ useModelGeometry: true });
    };
    ToolbarService.prototype.exportPNG = function (paper) {
        return __awaiter(this, void 0, void 0, function () {
            var _a, _b, _c;
            var _d;
            return __generator(this, function (_e) {
                switch (_e.label) {
                    case 0:
                        paper.hideTools();
                        _b = (_a = joint.format).toPNG;
                        _c = [paper, function (dataURL) {
                                new joint.ui.Lightbox({
                                    image: dataURL,
                                    downloadable: true,
                                    fileName: 'joint-plus',
                                }).open();
                                paper.showTools();
                            }];
                        _d = {
                            padding: 10,
                            useComputedStyles: false,
                            grid: true
                        };
                        return [4 /*yield*/, fontsStyleSheet()];
                    case 1:
                        _b.apply(_a, _c.concat([(_d.stylesheet = _e.sent(),
                                _d)]));
                        return [2 /*return*/];
                }
            });
        });
    };
    ToolbarService.prototype.exportSVG = function (paper) {
        return __awaiter(this, void 0, void 0, function () {
            var _a, _b, _c;
            var _d;
            return __generator(this, function (_e) {
                switch (_e.label) {
                    case 0:
                        paper.hideTools();
                        _b = (_a = joint.format).toSVG;
                        _c = [paper, function (svg) {
                                new joint.ui.Lightbox({
                                    image: 'data:image/svg+xml,' + encodeURIComponent(svg),
                                    downloadable: true,
                                    fileName: 'joint-plus'
                                }).open();
                                paper.showTools();
                            }];
                        _d = {
                            preserveDimensions: true,
                            convertImagesToDataUris: true,
                            useComputedStyles: false,
                            grid: true
                        };
                        return [4 /*yield*/, fontsStyleSheet()];
                    case 1:
                        _b.apply(_a, _c.concat([(_d.stylesheet = _e.sent(),
                                _d)]));
                        return [2 /*return*/];
                }
            });
        });
    };
    ToolbarService.prototype.openFileSelect = function (commandManager, graph) {
        var _this = this;
        if (this.openDropdowns.file) {
            joint.ui.ContextToolbar.close();
            return;
        }
        this.openDropdowns.file = true;
        var contextToolbar = new joint.ui.ContextToolbar({
            target: this.toolbar.getWidgetByName('select-file').el,
            root: this.toolbar.el,
            padding: 0,
            vertical: true,
            position: 'bottom-left',
            anchor: 'top-left',
            tools: this.fileTools
        });
        contextToolbar.on('action:load', function () {
            joint.ui.ContextToolbar.close();
            var fileInput = document.createElement('input');
            fileInput.setAttribute('type', 'file');
            fileInput.setAttribute('accept', '.json');
            fileInput.click();
            fileInput.onchange = function () {
                var file = fileInput.files[0];
                var reader = new FileReader();
                reader.onload = function (evt) {
                    var str = evt.target.result;
                    graph.fromJSON(JSON.parse(str));
                    commandManager.reset();
                };
                reader.readAsText(file);
            };
        });
        contextToolbar.on('action:save', function () {
            joint.ui.ContextToolbar.close();
            var str = JSON.stringify(graph.toJSON());
            var bytes = new TextEncoder().encode(str);
            var blob = new Blob([bytes], { type: 'application/json' });
            var el = window.document.createElement('a');
            el.href = window.URL.createObjectURL(blob);
            el.download = 'kitchensink.json';
            document.body.appendChild(el);
            el.click();
            document.body.removeChild(el);
        });
        contextToolbar.on('action:new', function () {
            joint.ui.ContextToolbar.close();
            graph.resetCells([]);
            commandManager.reset();
        });
        // Update openDropdowns state when the contextToolbar is closed
        contextToolbar.once('close', function () { return _this.openDropdowns.file = false; });
        contextToolbar.render();
    };
    ToolbarService.prototype.openLayoutSelect = function (graph, paperScroller) {
        var _this = this;
        if (this.openDropdowns.layout) {
            joint.ui.ContextToolbar.close();
            return;
        }
        this.openDropdowns.layout = true;
        var contextToolbar = new joint.ui.ContextToolbar({
            target: this.toolbar.getWidgetByName('select-layout').el,
            root: this.toolbar.el,
            padding: 0,
            vertical: true,
            position: 'bottom-left',
            anchor: 'top-left',
            tools: this.layoutTools
        });
        contextToolbar.on({
            'action:layout-tb': function () {
                _this.layoutDirectedGraph('TB', graph, paperScroller);
                joint.ui.ContextToolbar.close();
            },
            'action:layout-bt': function () {
                _this.layoutDirectedGraph('BT', graph, paperScroller);
                joint.ui.ContextToolbar.close();
            },
            'action:layout-lr': function () {
                _this.layoutDirectedGraph('LR', graph, paperScroller);
                joint.ui.ContextToolbar.close();
            },
            'action:layout-rl': function () {
                _this.layoutDirectedGraph('RL', graph, paperScroller);
                joint.ui.ContextToolbar.close();
            }
        });
        // Update openDropdowns state when the contextToolbar is closed
        contextToolbar.once('close', function () { return _this.openDropdowns.layout = false; });
        contextToolbar.render();
    };
    ToolbarService.prototype.openSettingsPopup = function (graph) {
        var _this = this;
        var inputs = this.getSettingsInspectorConfig().inputs;
        if (this.openDropdowns.settings) {
            joint.ui.Popup.close();
            return;
        }
        this.openDropdowns.settings = true;
        var settingsInspector = new joint.ui.Inspector({
            cell: graph,
            className: 'settings-inspector',
            inputs: inputs
        }).render();
        var settingsPopup = new joint.ui.Popup({
            content: settingsInspector.el,
            target: this.toolbar.getWidgetByName('select-canvas-settings').el,
            root: this.toolbar.el,
            padding: 0,
            position: 'bottom-right',
            anchor: 'top-right',
            autoResize: true,
            arrowPosition: 'none'
        }).render();
        settingsPopup.once('close', function () {
            settingsInspector.updateCell();
            // Update openDropdowns state when the settingsPopup is closed
            _this.openDropdowns.settings = false;
        });
    };
    ToolbarService.prototype.openShareSelect = function (paper) {
        var _this = this;
        if (this.openDropdowns.share) {
            joint.ui.ContextToolbar.close();
            return;
        }
        this.openDropdowns.share = true;
        var contextToolbar = new joint.ui.ContextToolbar({
            target: this.toolbar.getWidgetByName('select-share').el,
            root: this.toolbar.el,
            padding: 0,
            vertical: true,
            position: 'bottom-right',
            anchor: 'top-right',
            tools: this.shareTools
        });
        contextToolbar.on({
            'action:exportPNG': function () {
                _this.exportPNG(paper);
                joint.ui.ContextToolbar.close();
            },
            'action:exportSVG': function () {
                _this.exportSVG(paper);
                joint.ui.ContextToolbar.close();
            },
            'action:print': function () {
                joint.format.print(paper, { grid: true });
                joint.ui.ContextToolbar.close();
            }
        });
        // Update openDropdowns state when the contextToolbar is closed
        contextToolbar.once('close', function () { return _this.openDropdowns.share = false; });
        contextToolbar.render();
    };
    return ToolbarService;
}());
export { ToolbarService };
